import {
  skipToken,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  ChatChartSegment,
  ChatConversationDto,
  ChatConversationSummaryDto,
  ChatMemorySegment,
  ChatMessageDto,
  ChatMessageFeedbackDto,
  ChatSegment as ApiChatSegment,
  ChatTableSegment,
  ChatTextSegment,
  ChatToolActivityDto,
  ChatToolsSegment,
} from 'src/api/FiretrolApiClient';
import { chatbotApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';
import { logger } from 'src/utils';

export interface IChatToolActivity extends Partial<ChatToolActivityDto> {
  name: string;
  /** "running" until the tool's result arrives, then "done". */
  status: string;
}

/** What the reply did to the note of a memory segment. */
export type ChatMemoryAction = 'saved' | 'removed';

// Ordered pieces of an assistant reply: a run of streamed text, a run of
// tool calls made between two pieces of text, a chart, a table, or a memory
// note the reply saved or removed. Kept in the order they occurred so tool
// activity, charts and tables render interleaved with the text they relate to,
// rather than collapsing into one block at the top.
export type ChatSegment =
  | (ChatTextSegment & { kind: 'text' })
  | (Omit<ChatToolsSegment, 'activity'> & {
      kind: 'tools';
      activity: IChatToolActivity[];
    })
  | (ChatChartSegment & { kind: 'chart' })
  | (ChatTableSegment & { kind: 'table' })
  | (ChatMemorySegment & { kind: 'memory'; memoryAction: ChatMemoryAction });

/** Segments saved before the action was recorded carry none; they were all saves. */
export const toChatMemoryAction = (
  value: string | null | undefined,
): ChatMemoryAction => (value === 'removed' ? 'removed' : 'saved');

export interface IChatUiMessage {
  id: string;
  role: string;
  segments: ChatSegment[];
  streaming?: boolean;
  createdAt: Date;
  stopReason?: string;
  /** Set when a reply ended client-side without a 'done' event; never persisted, so it is lost on reload. */
  interruption?: 'stopped' | 'failed';
  feedback?: ChatMessageFeedbackDto;
  thoughtHarder?: boolean;
  /** Memory notes the reply reported using; the notes may have been deleted since. */
  usedMemoryIds?: number[];
}

/** Prefix of the ids the client gives a message before the server has sent its persisted id. */
export const LOCAL_MESSAGE_ID_PREFIX = 'local-';

/** False while the message still carries its local id, which every message endpoint answers with 404. */
export const hasServerId = (message: IChatUiMessage): boolean =>
  !message.id.startsWith(LOCAL_MESSAGE_ID_PREFIX);

export const messageText = (message: IChatUiMessage): string =>
  message.segments
    .filter(
      (segment): segment is Extract<ChatSegment, { kind: 'text' }> =>
        segment.kind === 'text',
    )
    .map((segment) => segment.text)
    .join('\n\n');

export const messagesBefore = (
  messages: IChatUiMessage[],
  messageId: string,
): IChatUiMessage[] => {
  const index = messages.findIndex((m) => m.id === messageId);
  return index < 0 ? messages : messages.slice(0, index);
};

const toUiSegment = (segment: ApiChatSegment): ChatSegment[] => {
  const known = segment as ChatSegment;
  switch (known.kind) {
    case 'tools':
      return [
        {
          ...known,
          activity: known.activity.map((activity) => ({
            ...activity,
            auditId: activity.auditId ?? undefined,
            status: 'done',
          })),
        },
      ];
    case 'memory':
      return known.memory
        ? [{ ...known, memoryAction: toChatMemoryAction(known.memoryAction) }]
        : [];
    case 'chart':
      return known.chart ? [known] : [];
    case 'table':
      return known.table ? [known] : [];
    case 'text':
      return [known];
    default:
      logger.warn('Ignoring a chat message segment of an unknown kind', {
        kind: segment.kind,
      });
      return [];
  }
};

/**
 * Reconstructs ordered UI segments from a persisted message. Falls back to a
 * single text segment when `segments` is absent/empty, as on user messages,
 * which are always plain text. Shared between history-load and the admin
 * replay view (`ConversationDetailModal`) so the two call sites don't diverge.
 */
export const buildMessageSegments = (message: ChatMessageDto): ChatSegment[] =>
  message.segments?.length
    ? message.segments.flatMap(toUiSegment)
    : [{ kind: 'text', text: message.text }];

// Replies are sent either at the configured default effort or at the Think
// harder effort, so any recorded effort other than the default is Think harder.
const DEFAULT_REASONING_EFFORT = 'low';

const usedThinkHarder = (reasoningEffort: string | undefined): boolean =>
  !!reasoningEffort &&
  reasoningEffort.trim().toLowerCase() !== DEFAULT_REASONING_EFFORT;

const buildUiMessage = (message: ChatMessageDto): IChatUiMessage => ({
  id: message.id,
  role: message.role,
  segments: buildMessageSegments(message),
  // The generated client types dates as Date but returns the raw JSON string.
  createdAt: new Date(message.createdAt),
  stopReason: message.stopReason,
  feedback: message.feedback ?? undefined,
  thoughtHarder: usedThinkHarder(message.reasoningEffort),
  usedMemoryIds: message.usedMemoryIds ?? [],
});

export const toConversationSummary = ({
  messages: _messages,
  ...summary
}: ChatConversationDto): ChatConversationSummaryDto => summary;

export const toUiMessages = (
  conversation: ChatConversationDto,
): IChatUiMessage[] =>
  conversation.messages.filter((m) => m.role !== 'system').map(buildUiMessage);

// Conversation list/history are read-mostly; cache them for a while instead
// of the app's usual gcTime:0 habit (see other useX hooks) since staleness
// here just means a slightly-out-of-date sidebar, not incorrect data.
export const CHAT_QUERY_STALE_TIME = 30_000;
export const CHAT_QUERY_CACHE_TIME = 10 * 60_000;

export function useConversationsQuery(
  enabled: boolean,
): UseQueryResult<ChatConversationSummaryDto[]> {
  return useQuery({
    queryKey: queryKeys.chat.conversations(),
    queryFn: async ({ signal }) => {
      const result = await chatbotApiClient.listConversations(
        1,
        50,
        toCancelToken(signal),
      );
      return result.items ?? [];
    },
    enabled,
    staleTime: CHAT_QUERY_STALE_TIME,
    gcTime: CHAT_QUERY_CACHE_TIME,
    refetchOnWindowFocus: false,
  });
}

export interface IUseConversationMessagesQueryOptions {
  /**
   * Set false to suspend fetching without changing the query key — e.g.
   * while a message is streaming, so a background refetch can't race the
   * in-flight overlay. Cached data for the id (if any) keeps rendering; only
   * the fetch is suppressed. Defaults to true.
   */
  enabled?: boolean;
}

export function useConversationMessagesQuery(
  conversationId: string | null,
  options?: IUseConversationMessagesQueryOptions,
): UseQueryResult<IChatUiMessage[]> {
  const qc = useQueryClient();
  return useQuery({
    queryKey: queryKeys.chat.messages(conversationId ?? ''),
    queryFn: async ({ signal }) => {
      const conversation = await chatbotApiClient.getConversation(
        conversationId as string,
        toCancelToken(signal),
      );
      qc.setQueryData(
        queryKeys.chat.conversation(conversation.id),
        toConversationSummary(conversation),
      );
      return toUiMessages(conversation);
    },
    enabled: !!conversationId && (options?.enabled ?? true),
    staleTime: CHAT_QUERY_STALE_TIME,
    gcTime: CHAT_QUERY_CACHE_TIME,
    refetchOnWindowFocus: false,
  });
}

/**
 * The summary of a conversation as its last detail read returned it, for
 * fields such as `memoryOff` on a conversation outside the first list page.
 * It never fetches; `useConversationMessagesQuery` fills it.
 */
export function useConversationSummaryQuery(
  conversationId: string | null,
): UseQueryResult<ChatConversationSummaryDto> {
  return useQuery<ChatConversationSummaryDto>({
    queryKey: queryKeys.chat.conversation(conversationId ?? ''),
    queryFn: skipToken,
    staleTime: Infinity,
    gcTime: CHAT_QUERY_CACHE_TIME,
  });
}

/**
 * Combines cached history with the locally-managed streaming overlay (the
 * just-sent user message plus the in-flight assistant reply). Overlay
 * entries win on id collision and any overlay entries not already in history
 * are appended in order, so a message never flashes/disappears while it
 * transitions from "streaming" to "persisted".
 */
export const mergeMessages = (
  history: IChatUiMessage[],
  overlay: IChatUiMessage[],
): IChatUiMessage[] => {
  if (overlay.length === 0) return history;
  const overlayIds = new Set(overlay.map((m) => m.id));
  return [...history.filter((m) => !overlayIds.has(m.id)), ...overlay];
};
